import './chatWithSummaryNew.css';
import { Button, SpaceBetween, Container } from '@cloudscape-design/components';
import { useContext, useRef, useState } from 'react';
import { UserContext } from '../../../context/authContext';
import LazyLoadingPlaceholder from './loadingPlaceHolder';
import { ScrollDownArrow } from '../shared/scrollDownArrow';
import { useAlert } from '../../../context/alertContext';

interface IPrevAnswer {
  Document: {
    documentFileName: string;
    _id: string;
  };
  answer: string;
  originalCitations: any[];
  originalHardCitations: any[];
  originalInternalAnswer: string;
  score: number;
  text: string;
}

interface GeneralChatProps {}

export const PreviousSearch: React.FC<GeneralChatProps> = ({}) => {
  const { token } = useContext(UserContext);
  const { setAlert } = useAlert();
  const containerRef = useRef<HTMLDivElement>(null);
  //! state
  const [inputText, setInputText] = useState<string>('');
  const [messages, setMessages] = useState<IPrevAnswer[]>([]);

  const [requestLoading, setRequestLoading] = useState<boolean>(false);
  const allInfoRef = useRef<IPrevAnswer[]>([]);
  const [jobComplete, setJobComplete] = useState<boolean>(false);

  // coins state

  const reset = () => {
    setMessages([]);
    setRequestLoading(false);
    allInfoRef.current = [];
    setJobComplete(false);
    setInputText('');
  };

  const searchAndAwaitPreviousAnswers = async () => {
    setRequestLoading(true);
    setMessages([]);
    setRequestLoading(true);
    allInfoRef.current = [];
    setJobComplete(false);

    const customHeaders = {
      Authorization: `Bearer ${token}`
    };
    const queryParams = new URLSearchParams(customHeaders).toString();
    const ws = new WebSocket(`${process.env.REACT_APP_WS}?${queryParams}`);
    ws.onopen = () => {
      console.log('WebSocket connection established');

      ws.send(
        JSON.stringify({
          action: 'prevQuestion',
          message: [
            {
              text: inputText,
              _id: 'testId',
              messageToken: `${token}`
            }
          ]
        })
      );
    };
    // Handle incoming messages
    ws.onmessage = (event) => {
      console.log('Message received:', event.data);
      const parsedData = JSON.parse(event.data);
      console.log('parsed data', parsedData);
      if (parsedData.type === 'single-prev-answer') {
        console.log('prev answer', parsedData.data);
        setMessages((prev) => [...prev, parsedData.data]);
        setRequestLoading(false);
      }
      // }
      if (parsedData.type === 'search-complete') {
        setJobComplete(true);
        setRequestLoading(false);
        ws.close();
      }
    };
    ws.onerror = (error) => {
      console.error('WebSocket error:', error);
      setAlert('Error getting answers', 'error', 3);
      setRequestLoading(false);
    };
    // Handle connection close
    ws.onclose = () => {
      console.log('WebSocket connection closed');
    };
  };
  // /app/rfp-upload?documentId=68f2713c9ce62ef4d6d81f88
  const handleShowOriginalDoc = (id: string) => {
    const url = `${process.env.REACT_APP_BASE_URL}/app/rfp-upload?documentId=${id}`;
    window.open(url, '_blank');
  };

  // // testing the 800 char question websocket call

  return (
    <div>
      <div
        className="relative h-screen overflow-y-auto p-4 "
        ref={containerRef}
        style={{ scrollbarWidth: 'none' }}
      >
        <Container>
          <SpaceBetween size={'xs'}>
            <div className="pb-6 border-gray-200">
              <h1 className="text-3xl font-bold text-gray-900 mb-3">
                KORU Answer Library
              </h1>
              <div className="w-full">
                <p className="text-gray-600">
                  Search your previous Answers, find previous similar responses.
                </p>
              </div>
            </div>
            <Container
              footer={
                <div className="flex flex-row justify-end p-1 gap-4">
                  <Button onClick={() => reset()}>Reset</Button>
                  <Button
                    onClick={() => {
                      searchAndAwaitPreviousAnswers();
                    }}
                    loading={requestLoading}
                    disabled={inputText.length === 0}
                  >
                    Send
                  </Button>
                </div>
              }
            >
              <SpaceBetween direction="vertical" size={'xs'}>
                <div className="flex gap-4">
                  <input
                    type="text"
                    value={inputText}
                    onChange={(e) => setInputText(e.target.value)}
                    className="flex-1 px-4 py-2 border rounded-md bg-blue-50"
                    placeholder={'What are you looking for ?'}
                  />
                </div>
              </SpaceBetween>
            </Container>

            {requestLoading && !jobComplete ? (
              <LazyLoadingPlaceholder />
            ) : (
              <>
                {messages.length > 0 &&
                  messages.map((message, index) => {
                    return (
                      <div
                        key={`${message.Document._id}-${index}`}
                        className="bg-white border border-slate-200 rounded-xl shadow-sm p-5 transition hover:shadow-md"
                      >
                        <div className="flex items-center justify-between mb-3">
                          <span className="inline-flex items-center rounded-full bg-indigo-100 px-3 py-1 text-xs font-semibold text-indigo-700">
                            Previous Result #{index + 1}
                          </span>
                          <span
                            className="rounded bg-slate-100 px-2 py-1 font-mono text-[11px] hover:cursor-pointer"
                            onClick={() =>
                              handleShowOriginalDoc(message.Document._id)
                            }
                          >
                            {'View Original'}
                          </span>
                        </div>

                        <div className="space-y-3 text-sm text-slate-700">
                          <div>
                            <p className="text-xs uppercase tracking-wide text-slate-500">
                              Original Question
                            </p>
                            <p className="mt-1 font-medium leading-relaxed">
                              {message.text || 'No question text available.'}
                            </p>
                          </div>

                          <div>
                            <p className="text-xs uppercase tracking-wide text-slate-500">
                              Original Answer
                            </p>
                            <p className="mt-1 leading-relaxed">
                              {message.answer || 'No answer captured.'}
                            </p>
                          </div>

                          <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500">
                            <span className="uppercase tracking-wide">
                              Document Name
                            </span>
                            <span className="rounded bg-slate-100 px-2 py-1 font-mono text-[11px]">
                              {message.Document.documentFileName || 'N/A'}
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
              </>
            )}
            {jobComplete && messages.length === 0 && (
              <div className="mt-6 rounded-xl border border-dashed border-slate-300 bg-slate-50 px-6 py-10 text-center text-slate-600">
                <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-white shadow-sm">
                  <span className="text-xl">🔍</span>
                </div>
                <h3 className="text-lg font-semibold text-slate-700">
                  No previous responses found
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-500">
                  Try adjusting your prompt or expand the keywords to surface
                  matching historical answers.
                </p>
              </div>
            )}
          </SpaceBetween>
        </Container>

        <ScrollDownArrow
          containerRef={containerRef as React.RefObject<HTMLElement>}
          bottomValue="8%"
        />
      </div>
    </div>
  );
};
